/**
 * Analytics Service - Session-based anonymous tracking
 * 
 * This service provides anonymous analytics tracking across KrUltra Website and RunnersHub
 * without any user identification, eliminating GDPR concerns.
 * 
 * Uses singleton pattern to maintain a single session per app lifecycle.
 * Events are sent immediately to Firestore (no batching) to prevent data loss.
 */

import { collection, doc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export type AppType = 'web' | 'rh';

export interface AnalyticsEventData {
  page?: string;
  action?: string;
  step?: number;
  eventId?: string;
  registrationId?: string;
  amount?: number;
  duration?: number;
  userAgent?: string;
  referrer?: string;
  locale?: string;
  isMobile?: boolean;
  isAdmin?: boolean;
}

export interface AnalyticsEvent {
  timestamp: Timestamp;
  sessionId: string;
  app: AppType;
  type: 'page_view' | 'registration_event' | 'payment_event' | 'session_event';
  isAdmin: boolean;
  data: AnalyticsEventData;
}

class AnalyticsService {
  private app: AppType;
  private sessionId: string;
  private sessionStartTime: number = Date.now();
  private initialized: boolean = false;
  private adminFlag: boolean = false;

  constructor(app: AppType) {
    this.app = app;
    this.sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

  /**
   * Initialize session tracking (call once when app mounts)
   */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.sessionStartTime = Date.now();

    // Track session start
    this.sendEvent('session_event', {
      action: 'session_start',
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      referrer: typeof window !== 'undefined' ? document.referrer : undefined,
      locale: typeof window !== 'undefined' ? navigator.language : undefined,
      isMobile: typeof window !== 'undefined' ? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent) : undefined,
    });

    // Track session end on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.sendEventSync('session_event', {
          action: 'session_end',
          duration: Date.now() - this.sessionStartTime
        });
      });
    }
  }

  /**
   * Track a page view
   */
  trackPageView(page: string): void {
    this.sendEvent('page_view', { page });
  }

  /**
   * Track a registration event
   */
  trackRegistrationEvent(action: string, data?: Partial<AnalyticsEventData>): void {
    this.sendEvent('registration_event', {
      action,
      ...data
    });
  }

  /**
   * Track a payment event
   */
  trackPaymentEvent(action: string, data?: Partial<AnalyticsEventData>): void {
    this.sendEvent('payment_event', {
      action,
      ...data
    });
  }

  /**
   * Mark this session as belonging to an admin user.
   * All subsequent events will be tagged with isAdmin: true.
   */
  setAdminFlag(isAdmin: boolean): void {
    this.adminFlag = isAdmin;
  }

  /**
   * Send a single event to Firestore immediately
   */
  private async sendEvent(type: AnalyticsEvent['type'], data: AnalyticsEventData): Promise<void> {
    const event: AnalyticsEvent = {
      timestamp: serverTimestamp() as Timestamp,
      sessionId: this.sessionId,
      app: this.app,
      type,
      isAdmin: this.adminFlag,
      data
    };

    try {
      const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const eventsCollectionRef = collection(db, 'analytics', 'events', date);
      const eventRef = doc(eventsCollectionRef, this.sessionId + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5));
      await setDoc(eventRef, event);
    } catch (error) {
      console.error('Analytics: Failed to send event:', error);
    }
  }

  /**
   * Send event synchronously using sendBeacon (for beforeunload)
   * Falls back to fire-and-forget Firestore write
   */
  private sendEventSync(type: AnalyticsEvent['type'], data: AnalyticsEventData): void {
    // Fire-and-forget: start the Firestore write but don't await
    this.sendEvent(type, data);
  }

  /**
   * Get current session ID (for debugging)
   */
  getSessionId(): string {
    return this.sessionId;
  }
}

// Singleton instance for RunnersHub
const rhAnalytics = new AnalyticsService('rh');
export { rhAnalytics };
export default AnalyticsService;
